/** localStorage persistence for the Pro license and saved schema templates. */
import type { ColumnType } from './types';

const PRO_KEY = 'chaosmonkey.pro.v1';
const TEMPLATES_KEY = 'chaosmonkey.templates.v1';

export interface ProState {
  key: string;
  activatedAt: number;
}

export interface SavedColumn {
  name: string;
  key: string;
  type: ColumnType;
}

export interface SchemaTemplate {
  id: string;
  name: string;
  tableName: string;
  createdAt: number;
  columns: SavedColumn[];
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — non-fatal */
  }
}

export function loadPro(): ProState | null {
  const v = readJson<ProState>(PRO_KEY);
  return v && typeof v.key === 'string' ? v : null;
}

export function savePro(state: ProState): void {
  writeJson(PRO_KEY, state);
}

export function clearPro(): void {
  try {
    localStorage.removeItem(PRO_KEY);
  } catch {
    /* ignore */
  }
}

export function loadTemplates(): SchemaTemplate[] {
  const v = readJson<SchemaTemplate[]>(TEMPLATES_KEY);
  if (!Array.isArray(v)) return [];
  return v.filter((t) => t && typeof t.id === 'string' && Array.isArray(t.columns));
}

export function saveTemplate(template: SchemaTemplate): SchemaTemplate[] {
  const all = loadTemplates();
  all.unshift(template);
  writeJson(TEMPLATES_KEY, all);
  return all;
}

export function deleteTemplate(id: string): SchemaTemplate[] {
  const next = loadTemplates().filter((t) => t.id !== id);
  writeJson(TEMPLATES_KEY, next);
  return next;
}
